// Tiny markdown → safe-HTML renderer for chat bubbles.
//
// Why hand-rolled instead of marked / micromark: the widget bundle
// is sensitive to size (one extra dependency adds ~30 KB minified)
// and the bot only produces a narrow subset of markdown — bold,
// italic, inline code, links, headings, ordered + unordered lists,
// paragraph breaks. We don't try to parse blockquotes, tables,
// fenced code blocks, or images; those don't appear in chat
// responses today.
//
// Safety: we escape every input character before applying markdown
// transforms. Reverse order would let an attacker craft a message
// like "<b>hi</b>" and have it render as live HTML. After escaping,
// the markdown rules only insert tags we control.

const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

export function escapeHTML(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ESCAPE_MAP[ch] ?? ch);
}

// renderMarkdown converts a subset of markdown to safe HTML. The
// input is treated as untrusted: all HTML special characters are
// escaped before markdown rules apply, so the only tags in the
// output are ones this function emits.
export function renderMarkdown(input: string): string {
  const escaped = escapeHTML(input);
  const lines = escaped.split(/\r?\n/);

  const out: string[] = [];
  let listType: "ul" | "ol" | null = null;
  let paragraph: string[] = [];

  function flushParagraph() {
    if (paragraph.length > 0) {
      out.push(`<p>${applyInline(paragraph.join(" "))}</p>`);
      paragraph = [];
    }
  }
  function openList(kind: "ul" | "ol") {
    if (listType !== kind) {
      closeList();
      out.push(`<${kind}>`);
      listType = kind;
    }
  }
  function closeList() {
    if (listType) {
      out.push(`</${listType}>`);
      listType = null;
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line === "") {
      flushParagraph();
      closeList();
      continue;
    }
    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length;
      out.push(`<h${level}>${applyInline(heading[2])}</h${level}>`);
      continue;
    }
    const ol = line.match(/^\s*\d+\.\s+(.+)$/);
    if (ol) {
      flushParagraph();
      openList("ol");
      out.push(`<li>${applyInline(ol[1])}</li>`);
      continue;
    }
    const ul = line.match(/^\s*[-*]\s+(.+)$/);
    if (ul) {
      flushParagraph();
      openList("ul");
      out.push(`<li>${applyInline(ul[1])}</li>`);
      continue;
    }
    // Default: accumulate into a paragraph buffer so successive
    // non-empty lines stay joined by a space rather than producing
    // one <p> per line.
    closeList();
    paragraph.push(line);
  }
  flushParagraph();
  closeList();

  return out.join("");
}

function applyInline(s: string): string {
  // Inline code first: protects its contents from bold/italic
  // mangling without escaping HTML inside (already escaped upstream).
  let t = s.replace(/`([^`]+)`/g, (_, code) => `<code>${code}</code>`);
  // Links — restricted to http(s) so attackers can't inject
  // javascript: URIs via crafted messages. Title attribute kept off
  // to avoid surprise tooltips.
  t = t.replace(
    /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
    (_, label, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );
  // Bold + italic. Bold first because **x** would otherwise be
  // consumed by the italic rule as two adjacent *x* spans.
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>");
  t = t.replace(/(^|[^_])_([^_]+)_/g, "$1<em>$2</em>");
  return t;
}
